import { AxiosInstance } from "axios";
import { failure, isOk, ok, pending, Result } from "../../common/common";
import { makeAutoObservable } from "mobx";
import {
  FollowUp,
  FollowUpData,
  FollowUpStatus,
  FollowUpType,
  getDecisionAttachmentsDescription,
  getDecisionName,
  getDecisionSendToBRDescription,
  requiredAttachmentLabel,
} from "../../dbot/common/commonDomain";
import { DecisionType } from "../../common/decisions";
import { adminFollowUpMatters, navigateToProjectEvent } from "../../utils/navigation";
import { TFunction, TMessage, TMessageParam, TMessageParams } from "../../i18n/tt";
import { DocumentType, getDocumentLabel } from "../../common/documents";
import {
  getCommonFollowUpStatusLabel,
  getSubscribersStatusLabel,
  getSubscriptionFormStatusLabel,
} from "../../dbot/summary/summaryDomain";
import { TMessageKey } from "../../i18n/types.gen";
import { RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import {
  extractPortfolioFollowUpMattersUrl,
  SelectedPortfolioOption,
  sumFollowUpCounts,
} from "../portfolioDomain";
import { AccessType } from "../../types/enums/AccessType";
import { logError } from "../../utils/log";
import UserStore from "../../stores/UserStore";
import AlertMessageStore, { AlertMessageText } from "../../stores/AlertMessageStore";

export class PortfolioDecisionsStore {
  readonly api: PortfolioDecisionsApi;
  readonly userStore: UserStore;
  readonly messageStore: AlertMessageStore;
  decisionsResponse?: Result<GetPortfolioDecisionsResponse>;
  textFilter: StringFieldModel = new StringFieldModel();
  companyFilter?: RadioFieldModel<string | null>;
  t: (keyOrMsg: TMessageKey | TMessage | TMessageParam, params?: TMessageParams) => string;
  showDismissDecisionDialogForDecision?: PortfolioDecision;

  constructor(
    http: AxiosInstance,
    userStore: UserStore,
    messageStore: AlertMessageStore,
    t: (keyOrMsg: TMessageKey | TMessage | TMessageParam, params?: TMessageParams) => string,
  ) {
    this.api = new PortfolioDecisionsApi(http);
    this.userStore = userStore;
    this.messageStore = messageStore;
    this.t = t;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *dismissDecision(
    portfolio: SelectedPortfolioOption,
    decision: PortfolioDecision,
    contextCompany?: string,
    successMessage?: AlertMessageText,
  ) {
    try {
      yield this.api.updateDecisionDismissedStatus(decision, true);
      this.closeDismissDecisionDialog();
      if (successMessage) {
        this.messageStore.showSuccess(successMessage, { autoHide: false });
      }
      this.fetchDecisions(portfolio, contextCompany);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *undoDismissDecision(
    portfolio: SelectedPortfolioOption,
    decision: PortfolioDecision,
    contextCompany?: string,
  ) {
    this.messageStore.hideMessage();
    try {
      yield this.api.updateDecisionDismissedStatus(decision, false);
      this.fetchDecisions(portfolio, contextCompany);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *fetchDecisions(portfolio: SelectedPortfolioOption, contextCompany?: string) {
    this.decisionsResponse = pending();
    try {
      const response: GetPortfolioDecisionsResponse | null =
        yield this.api.fetchDecisionsForPortfolio(portfolio, contextCompany);
      if (!response) {
        logError("Failed to fetch portfolio decisions", { portfolio });
        return;
      }

      this.decisionsResponse = ok(response);

      this.userStore.setPortfolioFollowUpCount(sumFollowUpCounts(response));

      const companyMap = new Map(
        response.decisions.map((decision) => [decision.orgNo, decision.companyName]),
      );

      this.companyFilter = new RadioFieldModel<string | null>({
        options: [null, ...Array.from(companyMap.keys())],
        value: null,
        idFn: (o) => (o ? o : "all"),
        labelFn: (o) =>
          o ? { translated: companyMap.get(o)! } : { key: "portfolio.all-companies" },
      });
    } catch (e) {
      this.decisionsResponse = failure();
    }
  }

  getDecisions(statusFilter: FollowUpStatusFilter): PortfolioDecision[] {
    if (!isOk(this.decisionsResponse)) return [];
    const decisions = this.decisionsResponse.data?.decisions || [];

    return filterDecisionsByStatus(decisions, statusFilter);
  }

  get numberOfDecisionsByStatus(): Map<FollowUpStatusFilter, number> | undefined {
    if (isOk(this.decisionsResponse)) {
      const decisions = this.decisionsResponse.data.decisions;
      return new Map(
        followUpStatusFilters.map((filter) => [
          filter,
          filterDecisionsByStatus(decisions, filter).length,
        ]),
      );
    } else {
      return undefined;
    }
  }

  get showAdminLinks() {
    return this.userStore.hasAnyAdminRole;
  }

  showDismissDecisionDialog(decision: PortfolioDecision) {
    this.showDismissDecisionDialogForDecision = decision;
  }

  closeDismissDecisionDialog() {
    this.showDismissDecisionDialogForDecision = undefined;
  }
}

function filterDecisionsByStatus(decisions: PortfolioDecision[], filter: FollowUpStatusFilter) {
  switch (filter) {
    case "for-followup":
      return decisionsForFollowup(decisions);
    case "sent":
      return decisionsSent(decisions);
    case "for-signing":
      return decisionsForSigning(decisions);
    case "br-processing":
      return decisionsBrProcessing(decisions);
    case "approved":
      return decisionsApproved(decisions);
    case "completed":
      return decisionsCompleted(decisions);
  }
}

function decisionsForFollowup(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter((f) => f.status === FollowUpStatus.READY && f.isActive),
    }))
    .filter((d) => d.followUps.length > 0);
}

function decisionsSent(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter(
        (f) => f.data.type === "sendToBR" && f.data.brStatus === "SENT_TO_BR",
      ),
    }))
    .filter((d) => d.followUps.length > 0);
}

function decisionsForSigning(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter(
        (f) => f.data.type === "sendToBR" && f.data.brStatus === "SENT_FOR_SIGNING",
      ),
    }))
    .filter((d) => d.followUps.length > 0);
}

function decisionsBrProcessing(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter(
        (f) => f.data.type === "sendToBR" && f.data.brStatus === "PROCESSING",
      ),
    }))
    .filter((d) => d.followUps.length > 0);
}

function decisionsApproved(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter(
        (f) => f.data.type === "sendToBR" && f.data.brStatus === "BR_APPROVED",
      ),
    }))
    .filter((d) => d.followUps.length > 0);
}

function decisionsCompleted(decisions: PortfolioDecision[]) {
  return decisions
    .map((d) => ({
      ...d,
      followUps: d.followUps.filter((f) => f.data.type === "apply" && f.status === "COMPLETED"),
    }))
    .filter((d) => d.followUps.length > 0);
}

export const followUpStatusFilters = [
  "for-followup",
  "sent",
  "for-signing",
  "br-processing",
  "approved",
  "completed",
] as const;

export function getFollowUpStatusFilterLabel(filter: FollowUpStatusFilter): TMessageKey {
  switch (filter) {
    case "for-followup":
      return "portfolio.followup.tab.for-follow-up";
    case "sent":
      return "portfolio.followup.tab.sent";
    case "for-signing":
      return "portfolio.followup.tab.for-signing";
    case "br-processing":
      return "portfolio.followup.tab.br-processing";
    case "approved":
      return "portfolio.followup.tab.approved";
    case "completed":
      return "portfolio.followup.tab.completed";
  }
}

export function getEmptyStateLabel(filter: FollowUpStatusFilter): TMessageKey {
  switch (filter) {
    case "for-followup":
      return "portfolio.followup.empty.for-follow-up";
    case "sent":
      return "portfolio.followup.empty.sent";
    case "for-signing":
      return "portfolio.followup.empty.for-signing";
    case "br-processing":
      return "portfolio.followup.empty.br-processing";
    case "approved":
      return "portfolio.followup.empty.approved";
    case "completed":
      return "portfolio.followup.empty.completed";
  }
}

export type FollowUpStatusFilter = (typeof followUpStatusFilters)[number];

export function mapToFollowUpTab(tab?: string): FollowUpStatusFilter {
  return tab && followUpStatusFilters.some((s) => s === tab)
    ? (tab as FollowUpStatusFilter)
    : "for-followup";
}

export function mapToFollowUpUrl(
  portfolio: SelectedPortfolioOption,
  tab: FollowUpStatusFilter,
  contextCompany?: string,
): string {
  return contextCompany === undefined
    ? `${extractPortfolioFollowUpMattersUrl(portfolio)}/${tab}`
    : `${adminFollowUpMatters(contextCompany)}/${tab}`;
}

class PortfolioDecisionsApi {
  constructor(readonly http: AxiosInstance) {}

  async fetchDecisionsForPortfolio(
    portfolio: SelectedPortfolioOption,
    contextCompany?: string,
  ): Promise<GetPortfolioDecisionsResponse | null> {
    const url = toPortfolioDecisionsApiUrl(portfolio, contextCompany);
    return url ? this.http.get(url).then((res) => res.data) : null;
  }

  async updateDecisionDismissedStatus(
    decision: PortfolioDecision,
    dismissed: boolean = true,
  ): Promise<void> {
    return this.http
      .post(`/project/${decision.projectId}/dismiss-decision`, {
        decisionId: decision.decisionId,
        dismissed,
      })
      .then((res) => res.data);
  }
}

export type GetPortfolioDecisionsResponse = {
  decisions: PortfolioDecision[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
};

export type PortfolioDecision = {
  orgNo: string;
  companyName: string;
  projectName?: string;
  projectId: string;
  decisionId: string;
  decisionType: DecisionType;
  followUps: FollowUp[];
};

export function openProjectFollowUp(decision: PortfolioDecision, pathname: string) {
  return [
    navigateToProjectEvent(
      { orgNo: decision.orgNo, projectId: decision.projectId },
      {
        type: "project-navigation-state",
        from: pathname,
        view: {
          activeTab: "follow-up",
          displayMode: "overlay",
        },
      },
    ),
  ];
}

export function getFollowUpTypeLabel(followUp: FollowUpData): TMessage | undefined {
  switch (followUp.type) {
    case FollowUpType.allocatedShares:
      return { key: "portfolio.matters.allocatedShares" };
    case FollowUpType.apply:
      return { key: "portfolio.matters.apply" };
    case FollowUpType.attachment:
      return requiredAttachmentLabel(followUp.attachmentType);
    case FollowUpType.identification:
      return undefined; // not used
    case FollowUpType.viewSubscribers:
      return { key: "portfolio.matters.viewSubscribers" };
    case FollowUpType.sendToBR:
      return { key: "portfolio.matters.sendToBR" };
    case FollowUpType.subscriptionForm:
      return getDocumentLabel(DocumentType.subscription_form);
    case FollowUpType.subscriptionFormStatus:
      return { key: "portfolio.matters.subscriptionFormStatus" };
  }
}

export function getFollowUpStatusLabel(followUp: FollowUp): TMessage | undefined {
  switch (followUp.data.type) {
    case FollowUpType.viewSubscribers:
      return getSubscribersStatusLabel(followUp.data.numberOfSubscribers);
    case FollowUpType.subscriptionForm:
      return getSubscriptionFormStatusLabel(followUp, undefined);
    case FollowUpType.subscriptionFormStatus:
      return undefined;
    case FollowUpType.allocatedShares:
    case FollowUpType.attachment:
    case FollowUpType.identification:
    case FollowUpType.sendToBR:
      return getCommonFollowUpStatusLabel(followUp);
    case FollowUpType.apply:
      return getCommonFollowUpStatusLabel(followUp) ?? { key: "dbot.summary.completed" };
  }
}

export function getFollowUpInfoLabel(
  followUp: FollowUp,
  decision: PortfolioDecision,
): TMessage | undefined {
  if (followUp.status === FollowUpStatus.COMPLETED) return undefined;

  switch (followUp.data.type) {
    case FollowUpType.viewSubscribers:
      return { key: "dbot.summary.subscribersStatus.description.edit" };
    case FollowUpType.subscriptionForm:
      return { key: "dbot.summary.subscriptionForm.description.sendToSubscribers" };
    case FollowUpType.subscriptionFormStatus:
      return undefined;
    case FollowUpType.allocatedShares:
      return followUp.isActive
        ? { key: "dbot.summary.subscribersAllocation.description" }
        : { key: "dbot.summary.subscribersAllocation.description.disabled" };
    case FollowUpType.attachment:
      return getDecisionAttachmentsDescription(followUp.data.attachmentType);
    case FollowUpType.identification:
      return undefined;
    case FollowUpType.sendToBR:
      return followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.isSentToBR" }
        : getDecisionSendToBRDescription(decision.decisionType);
    case FollowUpType.apply:
      return { key: "dbot.summary.applyDecisionToSystem.tooltip" };
  }
}

const toPortfolioDecisionsApiUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  contextCompany?: string,
): string | null => {
  const format = (part: string, context: string = "") =>
    "/portfolio" + part.toLowerCase() + "/decisions" + context;
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("", contextCompany ? `/${contextCompany}` : "");
    case "with-admin-role":
      return format(`/role/${AccessType.ADMIN}`);
    case "with-board-role":
      return null;
    case "my-investments":
      return null;
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export function filterDecisions(
  decisions: PortfolioDecision[],
  filter: FollowUpStatusFilter,
  text: string,
  companyFilter: string | null,
  t: TFunction,
) {
  return filterDecisionsByOrgNo(
    filterDecisionsByText(filterDecisionsByStatus(decisions, filter), text, t),
    companyFilter,
  );
}

function filterDecisionsByOrgNo(decisions: PortfolioDecision[], companyFilter: string | null) {
  return companyFilter
    ? decisions.filter((decision) => decision.orgNo === companyFilter)
    : decisions;
}

function filterDecisionsByText(decisions: PortfolioDecision[], text: string, t: TFunction) {
  if (!text) return decisions;

  const searchString = text.toLowerCase();
  return decisions.filter(
    (decision) =>
      decision.companyName.toLowerCase().includes(searchString) ||
      t(getDecisionName(decision.decisionType)).toLowerCase().includes(searchString) ||
      decision.followUps.some((followUp) => {
        const typeLabel = getFollowUpTypeLabel(followUp.data);
        const statusLabel = getFollowUpStatusLabel(followUp);

        return (
          (typeLabel && t(typeLabel).toLowerCase().includes(searchString)) ||
          (statusLabel && t(statusLabel).toLowerCase().includes(searchString))
        );
      }),
  );
}
